import type { Building, IsoString } from '../types/index.js';
import { formatRelativeTime } from '../utils/index.js';

// Aggregated sensor state for one building.
// Populated from DeviceSensorPayload[] when WebSocket is connected;
// use computed values from mock data until then.
export interface BuildingSummary {
  onlineCount: number;
  totalCount: number;
  tempDisplay: string; // e.g. "22–28°C" or "72–82°F"
  humidityDisplay: string; // e.g. "45–60%"
  luxDisplay: string; // e.g. "200–800"
  occupancyDisplay: string; // e.g. "3/10 occupied"
  outletsDisplay: string; // e.g. "8 on / 12 off"
  lastUpdated: IsoString | null;
  activeAlertCount: number;
  highestAlertSeverity: 'critical' | 'warning' | 'info' | null;
}

interface BuildingCardProps {
  buildingId: string;
  building: Building;
  summary: BuildingSummary;
  onClick: (buildingId: string) => void;
}

const SEVERITY_BADGE: Record<NonNullable<BuildingSummary['highestAlertSeverity']>, string> = {
  critical: 'bg-danger text-white',
  warning: 'bg-warning text-white',
  info: 'bg-info text-white',
};

function AlertBadge({
  count,
  severity,
}: {
  count: number;
  severity: BuildingSummary['highestAlertSeverity'];
}) {
  if (count === 0 || !severity) {
    return (
      <span
        role="img"
        aria-label="No active alerts"
        className="flex h-5 w-5 items-center justify-center rounded-full bg-success"
      >
        <svg
          className="h-3 w-3 text-white"
          viewBox="0 0 12 12"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            d="M10 3L5 8.5 2 5.5"
            stroke="white"
            strokeWidth="1.5"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    );
  }
  return (
    <span
      className={`inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-xs font-bold ${SEVERITY_BADGE[severity]}`}
    >
      <span className="sr-only">
        {count} active {severity} alert{count !== 1 ? 's' : ''}
      </span>
      <span aria-hidden="true">{count}</span>
    </span>
  );
}

function OnlineBar({ onlineCount, totalCount }: { onlineCount: number; totalCount: number }) {
  const pct = totalCount > 0 ? Math.round((onlineCount / totalCount) * 100) : 0;
  return (
    <div
      role="progressbar"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`${onlineCount} of ${totalCount} devices online`}
      className="h-1.5 w-full overflow-hidden rounded-full bg-bg-elevated"
    >
      <div className="h-full rounded-full bg-success transition-all" style={{ width: `${pct}%` }} />
    </div>
  );
}

export function BuildingCard({ buildingId, building, summary, onClick }: BuildingCardProps) {
  const location = [building.address.city, building.address.state].filter(Boolean).join(', ');
  const statusDisplay =
    summary.onlineCount === summary.totalCount
      ? 'All online'
      : summary.onlineCount === 0
        ? 'All offline'
        : `${summary.onlineCount}/${summary.totalCount} online`;

  return (
    <button
      type="button"
      aria-label={`View ${building.name}`}
      onClick={() => onClick(buildingId)}
      className="group w-full cursor-pointer rounded-xl border border-border bg-bg-primary p-5 shadow-sm transition-all hover:border-accent/50 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-accent"
    >
      {/* Header row */}
      <div className="mb-1 flex items-start justify-between gap-2">
        <h2 className="text-base font-semibold leading-snug text-text-primary group-hover:text-accent">
          {building.name}
        </h2>
        <AlertBadge count={summary.activeAlertCount} severity={summary.highestAlertSeverity} />
      </div>

      {/* Location + full address tooltip */}
      {location && (
        <div className="group/info relative mb-3 flex items-center gap-1.5">
          <p className="text-sm text-text-secondary">{location}</p>
          <span
            aria-hidden="true"
            className="flex h-4 w-4 shrink-0 cursor-default items-center justify-center rounded-full border border-border bg-bg-elevated text-[10px] font-semibold text-text-secondary"
          >
            i
          </span>
          <div className="invisible absolute left-0 top-6 z-20 w-64 rounded-lg border border-border bg-bg-elevated p-3 shadow-md group-hover/info:visible">
            <p className="text-xs text-text-primary">{building.address.line1}</p>
            {building.address.line2 && (
              <p className="text-xs text-text-secondary">{building.address.line2}</p>
            )}
            <p className="text-xs text-text-secondary">
              {building.address.city}, {building.address.state} {building.address.postalCode}
            </p>
            <p className="text-xs text-text-secondary">{building.address.country}</p>
          </div>
        </div>
      )}

      {/* Device online count + bar */}
      <div className="mb-1 flex items-center justify-between text-sm">
        <span className="font-medium text-text-primary">{statusDisplay}</span>
        <span className="text-xs text-text-secondary">
          {summary.totalCount} device{summary.totalCount !== 1 ? 's' : ''}
        </span>
      </div>
      <OnlineBar onlineCount={summary.onlineCount} totalCount={summary.totalCount} />

      {/* Aggregated sensor stats */}
      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-text-secondary">
        <span>🌡 {summary.tempDisplay}</span>
        <span>💧 {summary.humidityDisplay}</span>
        <span>☀ {summary.luxDisplay}</span>
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-text-secondary">
        <span>Outlets: {summary.outletsDisplay}</span>
        <span>Occupied: {summary.occupancyDisplay}</span>
      </div>

      {/* Footer */}
      <p className="mt-3 text-xs text-text-secondary">
        {summary.lastUpdated ? `Updated ${formatRelativeTime(summary.lastUpdated)}` : 'No data yet'}
      </p>
    </button>
  );
}
